<template>
  <div class="space-y-3">
    <!-- Header: title + optional right-aligned actions -->
    <div v-if="title || subtitle || $slots.actions" class="flex items-center justify-between gap-3 flex-wrap">
      <div v-if="title || subtitle">
        <h2 v-if="title" class="text-xl font-semibold">{{ title }}</h2>
        <p v-if="subtitle" class="text-sm text-muted-color m-0 mt-0.5" data-testid="filter-bar-subtitle">{{ subtitle }}</p>
      </div>
      <div v-if="$slots.actions" class="flex items-center gap-2">
        <slot name="actions" />
      </div>
    </div>

    <!-- Filter card: search + view-specific filters on one row -->
    <div
      class="flex flex-col lg:flex-row lg:items-center gap-2 bg-white rounded-xl border border-gray-200 shadow-sm px-3 py-2"
    >
      <IconField iconPosition="left" class="flex-1 min-w-0">
        <InputIcon class="pi pi-search text-gray-400" />
        <InputText
          :modelValue="searchInput"
          :placeholder="searchPlaceholder"
          size="small"
          class="w-full !border-0 !shadow-none !bg-transparent !ring-0"
          @update:modelValue="onSearchInput"
          @keyup.enter="flushSearch"
        />
        <InputIcon
          v-if="searchInput"
          class="pi pi-times cursor-pointer text-muted-color hover:text-color"
          role="button"
          tabindex="0"
          aria-label="Clear search"
          data-testid="btn-clear-search"
          @click="clearSearch"
          @keydown.enter.prevent="clearSearch"
          @keydown.space.prevent="clearSearch"
        />
      </IconField>

      <div v-if="$slots.filters" class="hidden lg:block w-px self-stretch bg-gray-200 mx-1" />

      <div v-if="$slots.filters" class="flex items-center gap-2 flex-wrap">
        <slot name="filters" />
      </div>
    </div>

    <!-- Active-filter chips + result count -->
    <div
      v-if="chips.length || count"
      class="flex items-center justify-between gap-2 flex-wrap min-h-[1.5rem]"
    >
      <div class="flex items-center gap-2 flex-wrap">
        <Chip
          v-for="chip in chips"
          :key="chip.key"
          :label="chip.label"
          removable
          class="text-xs"
          @remove="emit('remove-chip', chip.key)"
        />
        <Button
          v-if="chips.length"
          label="Clear all"
          link
          size="small"
          class="!px-1 !py-0"
          @click="emit('clear')"
        />
      </div>
      <span v-if="count" class="text-xs text-gray-500 whitespace-nowrap">{{ count }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { debounce } from '@/utils';

interface FilterChip {
  key: string;
  label: string;
}

const props = withDefaults(
  defineProps<{
    /** Applied search term (v-model:search). */
    search?: string;
    searchPlaceholder?: string;
    title?: string;
    /** One plain-English line under the title saying what the page shows. */
    subtitle?: string;
    /** Optional muted result-count text, e.g. "Showing 1–25 of 1,204". */
    count?: string;
    /** Removable active-filter chips. */
    chips?: FilterChip[];
    debounceMs?: number;
  }>(),
  {
    search: '',
    searchPlaceholder: 'Search…',
    title: '',
    subtitle: '',
    count: '',
    chips: () => [],
    debounceMs: 350,
  },
);

const emit = defineEmits<{
  'update:search': [value: string];
  'remove-chip': [key: string];
  clear: [];
}>();

// Local mirror so typing feels instant while we debounce the applied value.
const searchInput = ref(props.search);
watch(
  () => props.search,
  (v) => {
    if (v !== searchInput.value) searchInput.value = v;
  },
);

const emitSearch = debounce((...args: unknown[]) => emit('update:search', args[0] as string), props.debounceMs);

function onSearchInput(val: string | undefined) {
  searchInput.value = val ?? '';
  emitSearch(searchInput.value.trim());
}

function flushSearch() {
  emit('update:search', searchInput.value.trim());
}

function clearSearch() {
  searchInput.value = '';
  emit('update:search', '');
}
</script>
